/**
 * Thin fetch wrapper around the versioned REST API.
 *
 * Every call goes to `/api/v1/...`: in development the Vite dev server proxies
 * that prefix to Django, in production Django serves both the SPA and the API
 * from the same origin, so no absolute URL is ever needed.
 *
 * Session and CSRF handling follow `docs/AUTH.md`: authentication is a
 * `sessionid` cookie, and every unsafe request (`POST`/`PATCH`/`DELETE`)
 * must echo the `csrftoken` cookie in an `X-CSRFToken` header.
 */
import { clearAll as clearOfflineQueue } from '../offline/queue';

import type { Paginated, ProfileUpdate, TaskStatus, User, UserRoleUpdate } from './types';

export const API_BASE_URL = '/api/v1';

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Error raised when the API answers with a non-2xx status. */
export class ApiError extends Error {
  readonly status: number;
  /**
   * The parsed JSON error body, when the response had one (e.g. a
   * `{code, detail}` conflict payload). `undefined` for a non-JSON or empty
   * body.
   */
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Payload returned by `GET /api/v1/health/`. */
export interface HealthResponse {
  status: string;
  version: string;
}

/**
 * Read a cookie by name.
 *
 * @param name - Cookie name.
 * @returns The cookie's decoded value, or `undefined` if it is not set.
 */
function readCookie(name: string): string | undefined {
  const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
}

let unauthorizedHandler: (() => void) | undefined;

/**
 * Register the callback invoked whenever an API call answers `401`, so the
 * app can clear its session state and send the volunteer back to
 * `/login`. Called once by `AuthProvider`.
 *
 * @param handler - Callback run on a `401` response, or `undefined` to clear it.
 */
export function setUnauthorizedHandler(handler: (() => void) | undefined): void {
  unauthorizedHandler = handler;
}

export interface ApiFetchOptions extends RequestInit {
  /** Skip the global 401 handler — used by the auth endpoints themselves. */
  skipUnauthorizedHandler?: boolean;
}

let csrfRequest: Promise<string | undefined> | undefined;

/**
 * Fetch `auth/csrf/` so the `csrftoken` cookie exists, and return its value.
 *
 * An unsafe request sent without that header is rejected by Django with a
 * `403`, which used to break the very first action of a cold session — most
 * visibly a magic link opened in a browser that had never loaded the app, so
 * verification failed and the volunteer was pushed back to the login screen.
 * Concurrent callers share one in-flight request.
 *
 * @returns The cookie's value, or `undefined` if the call failed.
 */
async function ensureCsrfCookie(): Promise<string | undefined> {
  csrfRequest ??= (async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/csrf/`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
    } catch {
      // Offline or unreachable: let the caller's own request surface it.
    }
    return readCookie('csrftoken');
  })().finally(() => {
    csrfRequest = undefined;
  });
  return csrfRequest;
}

/**
 * Perform a JSON request against the API.
 *
 * @param path - Path relative to `/api/v1`, for instance `health/`.
 * @param init - Extra `fetch` options, merged with the JSON defaults; set
 *   `skipUnauthorizedHandler` to bypass the global 401 handler (used by the
 *   login/verify calls, where a 401 is an expected outcome, not a session
 *   expiry).
 * @returns The parsed JSON body, or `undefined` for an empty (`204`) response.
 * @throws {ApiError} When the response status is not in the 2xx range.
 */
export async function apiFetch<T>(path: string, init: ApiFetchOptions = {}): Promise<T> {
  const { skipUnauthorizedHandler, headers, ...rest } = init;
  const method = (rest.method ?? 'GET').toUpperCase();
  const requestHeaders = new Headers({ Accept: 'application/json', ...headers });

  if (UNSAFE_METHODS.has(method)) {
    const csrfToken = readCookie('csrftoken') ?? (await ensureCsrfCookie());
    if (csrfToken) {
      requestHeaders.set('X-CSRFToken', csrfToken);
    }
    if (rest.body && !(rest.body instanceof FormData) && !requestHeaders.has('Content-Type')) {
      requestHeaders.set('Content-Type', 'application/json');
    }
  }

  const response = await fetch(`${API_BASE_URL}/${path.replace(/^\//, '')}`, {
    credentials: 'same-origin',
    headers: requestHeaders,
    ...rest,
  });

  if (response.status === 401 && !skipUnauthorizedHandler) {
    unauthorizedHandler?.();
  }

  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    parsed = undefined;
  }

  if (!response.ok) {
    throw new ApiError(response.status, `Requête API en échec (${response.status})`, parsed);
  }

  // `auth/csrf/` answers 200 with an empty body (it only sets a cookie),
  // and `auth/logout/` answers 204: treat any empty body as "no payload"
  // rather than assuming every 2xx response is JSON.
  if (!text) {
    return undefined as T;
  }
  return parsed as T;
}

/**
 * Perform a POST that answers a binary body (e.g. a CSV export) instead of
 * JSON — `apiFetch` always tries to parse the response as JSON, which a
 * file download is not. Shares `apiFetch`'s CSRF and session handling.
 *
 * @param path - Path relative to `/api/v1`.
 * @param body - The JSON-serialisable request body.
 * @returns The response body as a {@link Blob}, ready for
 *   `URL.createObjectURL`.
 * @throws {ApiError} When the response status is not in the 2xx range.
 */
export async function apiFetchPostForBlob(path: string, body: unknown): Promise<Blob> {
  const csrfToken = readCookie('csrftoken') ?? (await ensureCsrfCookie());
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (csrfToken) {
    headers.set('X-CSRFToken', csrfToken);
  }
  const response = await fetch(`${API_BASE_URL}/${path.replace(/^\//, '')}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify(body),
  });

  if (response.status === 401) {
    unauthorizedHandler?.();
  }
  if (!response.ok) {
    let parsedBody: unknown;
    try {
      parsedBody = JSON.parse(await response.text());
    } catch {
      parsedBody = undefined;
    }
    throw new ApiError(response.status, `Requête API en échec (${response.status})`, parsedBody);
  }
  return response.blob();
}

/**
 * Read the backend health endpoint.
 *
 * @returns The service status and the deployed version.
 * @throws {ApiError} When the backend is unreachable or answers an error.
 */
export function getHealth(): Promise<HealthResponse> {
  return apiFetch<HealthResponse>('health/');
}

/**
 * Fetch a fresh `csrftoken` cookie. Must be called once before the first
 * unsafe request of a session (see `docs/AUTH.md`).
 *
 * @returns Nothing: the side effect is the `csrftoken` cookie itself.
 */
export async function fetchCsrfToken(): Promise<void> {
  await apiFetch<void>('auth/csrf/', { skipUnauthorizedHandler: true });
}

/**
 * Request a magic link for an email address.
 *
 * @param email - The address to send the link to.
 * @returns Nothing: the API always answers `202`, whether or not the
 *   address is registered (see `docs/AUTH.md`).
 */
export async function requestMagicLink(email: string): Promise<void> {
  await apiFetch<void>('auth/magic-link/', {
    method: 'POST',
    body: JSON.stringify({ email }),
    skipUnauthorizedHandler: true,
  });
}

/**
 * Exchange a magic-link token for a session.
 *
 * @param token - The raw token from the `/verify?token=` URL.
 * @returns The signed-in user's profile.
 * @throws {ApiError} With status `400` if the token is unknown, expired or
 *   already used.
 */
export function verifyMagicLink(token: string): Promise<User> {
  return apiFetch<User>('auth/verify/', {
    method: 'POST',
    body: JSON.stringify({ token }),
    skipUnauthorizedHandler: true,
  });
}

/**
 * End the current session.
 *
 * Also empties the offline write queue (`docs/OFFLINE.md` §1: "no nominative
 * data at rest beyond what the screen needs... logging out clears both").
 * Whatever a volunteer had queued but not yet sent is lost — the same
 * volunteer would have to redo it after signing back in, but nothing of it
 * survives on a shared device once they sign out.
 *
 * @returns Nothing.
 */
export async function logout(): Promise<void> {
  await apiFetch<void>('auth/logout/', { method: 'POST' });
  await clearOfflineQueue();
}

/**
 * Fetch the signed-in user's own profile.
 *
 * @returns The current user's profile.
 * @throws {ApiError} With status `401` if there is no active session.
 */
export function getMe(): Promise<User> {
  return apiFetch<User>('auth/me/', { skipUnauthorizedHandler: true });
}

/**
 * Update the signed-in user's own profile (onboarding completion).
 *
 * @param update - The editable fields to change; see {@link ProfileUpdate}.
 * @returns The updated profile.
 */
export function patchMe(update: ProfileUpdate): Promise<User> {
  return apiFetch<User>('auth/me/', {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
}

/**
 * Read an asynchronous job's status. Reserved to national admins.
 *
 * @param taskId - The Celery task id.
 * @returns The job's current state and, once finished, its result.
 */
export function getTaskStatus(taskId: string): Promise<TaskStatus> {
  return apiFetch<TaskStatus>(`tasks/${encodeURIComponent(taskId)}/`);
}

/**
 * List users, for the admin accounts screen. Reserved to national admins.
 *
 * @param options - `pending: true` restricts the list to unvalidated accounts.
 * @returns A paginated page of users, per `docs/AUTH.md`.
 */
export function getUsers(options: { pending?: boolean } = {}): Promise<Paginated<User>> {
  const query = options.pending ? '?pending=1' : '';
  return apiFetch<Paginated<User>>(`users/${query}`);
}

/**
 * Approve a pending account. Reserved to national admins.
 *
 * @param id - Target user id.
 * @returns The now-validated user.
 */
export function validateUser(id: number): Promise<User> {
  return apiFetch<User>(`users/${id}/validate/`, { method: 'POST' });
}

/**
 * Change a user's role. Reserved to national admins.
 *
 * @param id - Target user id.
 * @param update - The role to set.
 * @returns The updated user.
 */
export function updateUserRole(id: number, update: UserRoleUpdate): Promise<User> {
  return apiFetch<User>(`users/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
}
